import { styled } from '@mui/material/styles'
import { Card, Menu, MenuItem, Typography, IconButton } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import InfoIcon from '@mui/icons-material/Info'
import DescriptionIcon from '@mui/icons-material/Description'
import StyleIcon from '@mui/icons-material/Style'
import ReplyIcon from '@mui/icons-material/Reply'
import {
  EPatientStatus,
  TRoomViewPatientStatusHistory,
} from '../RoomView.types'

export const StyledCard = styled(Card)<{
  status?: TRoomViewPatientStatusHistory['status']
}>(({ theme, status }) => ({
  width: '100%',
  minWidth: 160,
  maxWidth: 220,
  overflow: 'hidden',
  minHeight: 'fit-content',
  padding: '6px 8px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: 12,
  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  transition: 'all 0.2s ease-in-out',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transform: 'translateY(-1px)',
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: 3,
    borderRadius: '12px 0 0 12px',
    background:
      status === EPatientStatus.ASSIGN_ROOM
        ? theme.palette.primary.main
        : status === EPatientStatus.CHECKED_OUT
        ? theme.palette.secondary.main
        : theme.palette.primary.light,
  },
}))

export const WaitingPill = styled('div')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: 12,
  borderRadius: 4,
  minWidth: 0,
  textAlign: 'center',
}))

export const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 10,
    minWidth: 180,
    boxShadow: `
      0 0 0 1px rgba(0, 0, 0, 0.03),
      0 4px 24px rgba(0, 0, 0, 0.06),
      0 8px 48px rgba(0, 0, 0, 0.04)
    `,
    backdropFilter: 'blur(20px)',
    background: 'rgba(255, 255, 255, 0.98)',
    marginTop: 6,
    '& .MuiList-root': {
      padding: '6px 0',
    },
  },
}))

export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: 13.5,
  fontWeight: 500,
  padding: '8px 14px',
  margin: '0 6px',
  borderRadius: 8,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  letterSpacing: '-0.01em',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}08`,
    transform: 'translateY(-0.5px)',
  },
  '&:active': {
    backgroundColor: `${theme.palette.primary.main}12`,
    transform: 'translateY(0)',
  },
}))

export const DoctorName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: 12.5,
  letterSpacing: '-0.01em',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const Age = styled('span')(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  flexShrink: 0,
}))

export const NameAge = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: 'calc(100% - 12px)',
}))

export const PatientName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const ScheduledTime = styled(Typography)(({ theme }) => ({
  borderRadius: 1,
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.text.primary,
}))

export const StyledAccessTimeIcon = styled(AccessTimeIcon)(() => ({
  fontSize: 18,
  color: 'text.secondary',
  paddingBottom: '3px',
}))

export const StyledLocalHospitalIcon = styled(LocalHospitalIcon)(
  ({ theme }) => ({
    fontSize: 18,
    marginRight: theme.spacing(1),
  })
)

export const StyledInfoIcon = styled(InfoIcon)(({ theme }) => ({
  fontSize: 18,
  marginRight: theme.spacing(1),
}))

export const StyledDescriptionIcon = styled(DescriptionIcon)(({ theme }) => ({
  fontSize: 18,
  marginRight: theme.spacing(1),
}))

export const StyledStyleIcon = styled(StyleIcon)(({ theme }) => ({
  fontSize: 18,
  marginRight: theme.spacing(1),
}))

export const StyledBackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: 20,
  height: 20,
  borderRadius: 0,
  borderBottomLeftRadius: 8,
  color: 'white',
  opacity: 0.7,
  padding: 4,
  boxShadow: 'none',
  backgroundColor: theme.palette.primary.main,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: 'none',
    background: theme.palette.primary.dark,
  },
}))

export const StyledArrowBackIcon = styled(ReplyIcon)(({ theme }) => ({
  fontSize: 16,
}))

export const SurgeryName = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.text.secondary,
}))
